import type { RecordLocationRef, RecordLocationTarget, RecordTableRow } from './RecordsList';

/**
 * One status registration as served by `Records/status`. Snake_case because it mirrors the wire
 * verbatim.
 */
export type StatusApiRow = {
    record_id: number;
    /** Farm-local date of the registration. `yyyy-MM-dd`. */
    date: string;
    /**
     * Farm-local Monday of the week this registration bins into, `yyyy-MM-dd`. Server-computed, so
     * the page needs no week arithmetic of its own.
     */
    week_start: string;

    farm: RecordLocationRef;
    barn: RecordLocationRef;
    /**
     * Null when the registration was made at or above Barn level, or on a Batch that hangs under a
     * Barn rather than under a Section.
     */
    section: RecordLocationRef | null;
    /**
     * The registration's own resolved location, which may sit deeper than the Farm/Barn/Section
     * chain reaches (a Pen or a Batch) - carried on the wire but not read by this page's columns,
     * which render the Farm/Barn/Section chain instead (see `SoldRow`'s own `location`, typed the
     * same way, for the same pattern).
     */
    location: RecordLocationTarget;

    number_of_pigs: number;

    /** Null when no weight was registered for this row. */
    avg_weight: number | null;
    /** `avg_weight * number_of_pigs`, already derived server-side. */
    total_weight: number | null;
    /** Full name of the user who created the registration. Null when the creator is missing, deleted/anonymized, or has no name. */
    created_by: string | null;
};

/**
 * `StatusApiRow`, reshaped to the field names the shared records kit and `RecordsTable` read.
 *
 * `farm`/`barn`/`section` pass through unchanged so the page renders its Location and Section
 * columns the same way Sold/Slaughters do - through `RecordsTable`'s shared `barn` cell and
 * `locationLabel`, faceted on `barn.id` rather than a name, which can repeat across farms.
 * `avg_weight`/`total_weight` are already the figures `RecordRow`'s aggregation (the shared footer
 * totals) reads, so - unlike `SlaughterRow` - nothing here needs to null them out or fork its own
 * totals.
 */
export type StatusRow = RecordTableRow & {
    farm: RecordLocationRef;
    barn: RecordLocationRef;
    section: RecordLocationRef | null;
    location: RecordLocationTarget;
    /** Full name of the user who created the registration. Null when the creator is missing, deleted/anonymized, or has no name. */
    created_by: string | null;
};

/** `StatusApiRow` as the shared records kit and `RecordsTable` read it. */
export function mapStatusRow(raw: StatusApiRow): StatusRow {
    return {
        record_id: raw.record_id,
        date: raw.date,
        week_start: raw.week_start,
        number: raw.number_of_pigs,
        avg_weight: raw.avg_weight,
        total_weight: raw.total_weight,
        farm: raw.farm,
        barn: raw.barn,
        section: raw.section,
        location: raw.location,
        created_by: raw.created_by,
    };
}
